import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./GuideProfile.css";
import { 
  FaStar, 
  FaLanguage, 
  FaSuitcase, 
  FaAward, 
  FaCheckCircle,
  FaClock,
  FaMapMarkerAlt,
  FaGem,
  FaCreditCard,
  FaMoneyBillWave,
  FaShieldAlt,
  FaUserFriends,
  FaCalendarAlt,
  FaExclamationTriangle
} from "react-icons/fa";
import NavUser from "../components/NavUser";
import { fetchGuideAndTourDetails, fetchGuideExpertise } from "../services/guideService";
import currencyService from "../services/currencyService";
import { getCoinBalance } from "../services/coinService";
import { decodeToken } from "../services/userService";
import { getApiBaseUrl, getBackendBaseUrl } from "../utils/apiConfig";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DatePicker, InputNumber, Switch } from "antd";
import moment from "moment";
import { startKokoTourPayment } from "../services/hotelbookingService";
import PaymentService from "../services/PaymentService";
import SEO from "../components/SEO";

function GuideProfileBook() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const guideItem = React.useMemo(() => {
    return location.state?.guide || (id ? { id } : null);
  }, [location.state?.guide, id]);

  const [guideData, setGuideData] = useState(null);
  const [expertises, setExpertises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expertiseLoading, setExpertiseLoading] = useState(true);

  // Booking Flow States
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showPaymentSelect, setShowPaymentSelect] = useState(false);
  const [bookingDate, setBookingDate] = useState(null);
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [availableGuests, setAvailableGuests] = useState(10);
  const [isFreePickup, setIsFreePickup] = useState(false);
  const [specialRequests, setSpecialRequests] = useState("");
  const [coinBalance, setCoinBalance] = useState(0);
  const [applyRewards, setApplyRewards] = useState(false);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [pendingBooking, setPendingBooking] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("jwt_token");
    if (token) {
      const decoded = decodeToken(token);
      if (decoded?.id) setUserId(decoded.id);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      getCoinBalance(userId).then(setCoinBalance).catch(console.error);
    }
  }, [userId]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!guideItem?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await fetchGuideAndTourDetails(guideItem.id);
        setGuideData(data);

        if (data?.guide?.id) {
          try {
            setExpertiseLoading(true);
            const expertiseData = await fetchGuideExpertise(data.guide.id);
            setExpertises(expertiseData);
          } catch (error) {
            console.error("Failed to load guide expertises:", error);
          } finally {
            setExpertiseLoading(false);
          }
        }
      } catch (error) {
        console.error("Error fetching guide details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [guideItem]);

  if (loading) return (
    <div className="loading-state">
      <div className="spinner"></div>
      <p>Consulting with our expert guides...</p>
    </div>
  );

  if (!guideData) return (
    <div className="error-state">
      <h3>Guide details not found.</h3>
      <button onClick={() => navigate("/travelerhome")}>Browse Other Guides</button>
    </div>
  );

  const { guide, tour } = guideData;

  function getConvertedPrice() {
    return currencyService.convert(parseFloat(tour?.price) || 0.0, { 
      fromCurrency: currencyService.getCurrencyFromId(tour?.currency_id || 4) 
    });
  }

  async function onDateChange(date) {
    setBookingDate(date);
    if (!date) return;
    
    setLoadingGuests(true);
    try {
      const formattedDate = typeof date.format === "function" ? date.format("YYYY-MM-DD") : moment(date).format("YYYY-MM-DD");
      const response = await axios.get(`${getApiBaseUrl()}/guide-listing/tour-listings/${tour.id}/guest-count/${formattedDate}`);
      const available = response.data?.available_guests ?? response.data?.data?.available_guests ?? 10;
      setAvailableGuests(available);
    } catch (error) {
      console.error("Availability check failed:", error);
      toast.error("Could not check availability.");
    } finally {
      setLoadingGuests(false);
    }
  }

  async function handleConfirmBooking() {
    try {
      setLoading(true);
      const formattedDate = typeof bookingDate.format === "function" ? bookingDate.format("YYYY-MM-DD") : moment(bookingDate).format("YYYY-MM-DD");
      const currentUserId = userId || (localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")).id : null);
      const bookingData = {
        guide_listing_id: tour.id,
        user_id: currentUserId,
        booking_date: formattedDate,
        number_of_guests: numberOfGuests,
        pickup_requested: isFreePickup,
        special_requests: specialRequests,
        apply_rewards: applyRewards
      };

      const response = await axios.post(`${getApiBaseUrl()}/guide-booking/create`, bookingData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("jwt_token")}` }
      });

      if (response.data.success) {
        setPendingBooking(response.data.booking);
        setShowPaymentSelect(true);
        toast.success("Booking created! Select payment method.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Booking failed.");
    } finally {
      setLoading(false);
    }
  }

  async function handlePayment(method, isAdvance = false) {
    if (!pendingBooking) {
      toast.error("No active booking found. Please confirm reservation first.");
      return;
    }
    
    const finalAmount = Math.max(0, getConvertedPrice() - (applyRewards ? coinBalance : 0));
    const amountToPay = isAdvance ? (finalAmount / 2).toFixed(2) : finalAmount.toFixed(2);

    try {
      if (method === "payments_lk" || method === "card") {
        toast.info("Connecting to Payments.lk hosted checkout...");
        const session = await PaymentService.createPaymentsLkCheckout({
          bookingType: "tour",
          bookingId: pendingBooking.id,
          clientType: "web",
          isAdvance: isAdvance
        });

        if (session && session.checkoutUrl) {
          window.location.href = session.checkoutUrl;
        } else {
          toast.error("Failed to initialize Payments.lk checkout session.");
        }
      } else if (method === "koko") {
        toast.info("Connecting to Koko Gateway...");
        const formattedDate = typeof bookingDate.format === "function" ? bookingDate.format("YYYY-MM-DD") : moment(bookingDate).format("YYYY-MM-DD");
        const payload = {
          bookingId: pendingBooking.id,
          userId: pendingBooking.user_id || (localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")).id : null),
          tourListingId: tour.id,
          numberOfGuests: numberOfGuests,
          price: finalAmount,
          bookingDate: formattedDate,
          pickupRequested: isFreePickup ? 1 : 0,
          specialRequests: specialRequests,
          useCoinBalance: applyRewards ? 1 : 0,
          coins_used_amount: applyRewards ? Math.min(getConvertedPrice(), coinBalance) : 0
        };

        const data = await startKokoTourPayment(payload);
        if (!data || !data.actionUrl || !data.fields || Object.keys(data.fields).length === 0) {
          toast.error("Koko payment service is temporarily unavailable.");
          return;
        }

        const { actionUrl, fields } = data;

        const form = document.createElement("form");
        form.method = "POST";
        form.action = actionUrl;

        Object.entries(fields).forEach(([key, value]) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
      }
    } catch (error) {
      console.error("Payment initiation error:", error);
      toast.error("Payment initiation failed: " + (error.response?.data?.message || error.message));
    }
  }

  return (
    <div className="guide-profile-view">
      <SEO
        title={`${guide.name || "Local Tour Guide"} | Sri Lanka Tour Guide | Soloway`}
        description={guide.description ? guide.description.substring(0, 150) : `Book certified local tour guide ${guide.name || "in Sri Lanka"} on Soloway. Multilingual tours and local transport.`}
        canonicalPath={`/guideprofilebook/${guide.id || id || ""}`}
      />
      <NavUser />
      
      <div className="guide-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="profile-main-info">
            <div className="profile-avatar-wrapper">
              <img
                src={guide.logo ? (guide.logo.startsWith("http") ? guide.logo : `${getBackendBaseUrl()}${guide.logo}`) : "https://placehold.co/150x150?text=Expert"}
                alt={guide.name}
                className="profile-avatar-large"
              />
              <div className="verified-badge"><FaCheckCircle /></div>
            </div>
            <div className="profile-text-info">
              <h1>{guide.name || 'Professional Guide'}</h1>
              <div className="languages-tag">
                <FaLanguage />
                <span>Multilingual: {guide.languages || 'English'}</span>
              </div>
              <div className="experience-tag">
                <FaAward />
                <span>{guide.experience || 'Experienced'} in Hospitality</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="guide-view-container">
        <div className="guide-grid-layout">
          {/* Main Info */}
          <div className="guide-info-main">
            <section className="info-card section-animate">
              <div className="section-header">
                <FaSuitcase className="section-icon" />
                <h3>About the Guide</h3>
              </div>
              <p className="description-text">
                {guide.description || "Passionate about sharing the hidden gems and rich history of the region. Our guide brings years of local knowledge and a commitment to providing an authentic, safe, and memorable experience for every traveler."}
              </p>
            </section>

            <section className="info-card section-animate">
              <div className="section-header">
                <FaStar className="section-icon" />
                <h3>Expertise & Specialties</h3>
              </div>
              {expertiseLoading ? (
                <div className="skeleton-loader"></div>
              ) : (
                <div className="expertises-pills">
                  {expertises.length > 0 ? (
                    expertises.map((exp, index) => (
                      <span key={index} className="expertise-pill">
                        {exp.experties.name}
                      </span>
                    ))
                  ) : (
                    <p className="no-data">Traditional Heritage, Cultural Walks, Gastronomy</p>
                  )}
                </div>
              )}
            </section>

            <ReviewsSection tour={tour} />
          </div>

          {/* Sidebar Package */}
          <aside className="guide-sidebar">
            <div className="package-card sticky-package">
              {showPaymentSelect ? (
                <div className="payment-mode animate-fade-in">
                  <h1 className="booking-title1" style={{ fontSize: '26px', fontWeight: '800', color: '#2b6cb0', marginBottom: '8px', textAlign: 'left' }}>
                    Pay & Secure
                  </h1>
                  <p style={{ fontSize: '13px', color: '#718096', marginBottom: '20px', lineHeight: '1.4' }}>
                    Reservation #{pendingBooking?.id || ''} confirmed. Choose how you'd like to pay:
                  </p>

                  {(() => {
                    const finalPayAmount = Math.max(0, getConvertedPrice() - (applyRewards ? coinBalance : 0));
                    const currSymbol = currencyService.getCurrencySymbol(currencyService.selectedCurrency);
                    return (
                      <div className="payment-grid">
                        <div className="payment-option" onClick={() => handlePayment("payments_lk", false)}>
                          <div className="option-icon"><FaCreditCard /></div>
                          <div className="option-info">
                            <span className="option-title">Full Payment</span>
                            <span className="option-desc">Secure payment via Payments.lk</span>
                          </div>
                          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                            <div style={{ fontSize: '18px', fontWeight: '800', color: '#2b6cb0' }}>
                              {currSymbol} {finalPayAmount.toFixed(2)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#a0aec0' }}>Total Amount</div>
                          </div>
                        </div>

                        <div className="payment-option" onClick={() => handlePayment("payments_lk", true)}>
                          <div className="option-icon"><FaMoneyBillWave /></div>
                          <div className="option-info">
                            <span className="option-title">Advance Payment (50%)</span>
                            <span className="option-desc">Pay half now, balance on tour</span>
                          </div>
                          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                            <div style={{ fontSize: '18px', fontWeight: '800', color: '#38a169' }}>
                              {currSymbol} {(finalPayAmount / 2).toFixed(2)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#a0aec0' }}>Pay Now</div>
                          </div>
                        </div>

                        <div className="payment-option koko" onClick={() => handlePayment("koko")}>
                          <div className="option-icon" style={{ background: '#000', color: '#fff', fontSize: '18px', fontWeight: '900' }}>K.</div>
                          <div className="option-info">
                            <span className="option-title">Koko</span>
                            <span className="option-desc">3 Interest-free installments</span>
                          </div>
                          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                            <div style={{ fontSize: '18px', fontWeight: '800', color: '#000' }}>
                              {currSymbol} {(finalPayAmount / 3).toFixed(2)}
                            </div>
                            <div style={{ fontSize: '11px', color: '#a0aec0' }}>Per Month</div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="security-badge" style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <FaShieldAlt style={{ marginRight: '8px' }} /> 256-bit Secure SSL Encryption
                  </div>

                  <button className="back-btn-text" onClick={() => setShowPaymentSelect(false)}>
                    ← Back to Booking Details
                  </button>
                </div>
              ) : showBookingForm ? (
                <div className="booking-mode animate-fade-in">
                  <div className="package-badge">SCHEDULE EXPERIENCE</div>
                  <h3 className="package-title">Experience Details</h3>
                  
                  <div className="booking-field">
                    <label><FaCalendarAlt /> Preferred Date</label>
                    <DatePicker 
                      className="premium-datepicker" 
                      onChange={onDateChange} 
                      disabledDate={(current) => current && current < moment().endOf('day')}
                      placeholder="Select Date"
                    />
                  </div>

                  {bookingDate && (
                    <div className="booking-field animate-slide-down">
                      <label><FaUserFriends /> Travelers (Max {availableGuests})</label>
                      <InputNumber 
                        min={1} 
                        max={availableGuests} 
                        value={numberOfGuests} 
                        onChange={setNumberOfGuests} 
                        className="premium-input-number"
                      />
                      {availableGuests === 0 && (
                        <p className="availability-warning"><FaExclamationTriangle /> Fully Booked</p>
                      )}
                    </div>
                  )}

                  <div className="booking-field">
                    <div className="toggle-row">
                      <span><FaClock /> Pickup Service</span>
                      <Switch checked={isFreePickup} onChange={setIsFreePickup} />
                    </div>
                  </div>

                  {coinBalance > 0 && (
                    <div className="booking-field">
                      <div className="toggle-row">
                        <span><FaGem /> Apply Coins (Rs {coinBalance.toFixed(2)})</span>
                        <Switch checked={applyRewards} onChange={setApplyRewards} />
                      </div>
                    </div>
                  )}

                  <div className="booking-summary-small">
                    <div className="summary-row">
                      <span>Subtotal</span>
                      <span>{currencyService.getCurrencySymbol(currencyService.selectedCurrency)} {getConvertedPrice().toFixed(2)}</span>
                    </div>
                    {applyRewards && (
                      <div className="summary-row discount">
                        <span>Coin Discount</span>
                        <span>-{currencyService.getCurrencySymbol(currencyService.selectedCurrency)} {Math.min(getConvertedPrice(), coinBalance).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Total Amount</span>
                      <span>{currencyService.getCurrencySymbol(currencyService.selectedCurrency)} {Math.max(0, getConvertedPrice() - (applyRewards ? coinBalance : 0)).toFixed(2)}</span>
                    </div>
                  </div>

                  <button 
                    className="book-expert-btn" 
                    onClick={handleConfirmBooking}
                    disabled={!bookingDate || availableGuests === 0 || loadingGuests}
                  >
                    {loadingGuests ? 'Checking Slots...' : 'Confirm Reservation'}
                  </button>
                  <button className="back-btn-text" onClick={() => setShowBookingForm(false)}>
                    ← Back to Info
                  </button>
                </div>
              ) : (
                <>
                  <div className="package-badge">FEATURED EXPERIENCE</div>
                  <h3 className="package-title">{tour.title || 'Exclusive Local Tour'}</h3>
                  
                  <div className="package-price-area">
                    <div className="price-tag">
                      {currencyService.getCurrencySymbol(currencyService.selectedCurrency)} 
                      {getConvertedPrice().toFixed(2)}
                    </div>
                    <span className="price-label">Per Experience</span>
                  </div>

                  <div className="package-meta">
                    <div className="p-meta-item">
                      <FaClock />
                      <span>{tour.duration || 'Flexible'} Duration</span>
                    </div>
                    <div className="p-meta-item">
                      <FaMapMarkerAlt />
                      <span>{tour.service_area || 'Central Region'}</span>
                    </div>
                  </div>

                  <div className="package-includes">
                    <h4>What's Included</h4>
                    <ul className="includes-list">
                      <li><FaCheckCircle className="check-v" /> Professional Certified Guide</li>
                      <li><FaCheckCircle className="check-v" /> Local Insights & History</li>
                      {tour.inclusions?.map((item, index) => (
                        <li key={index}><FaCheckCircle className="check-v" /> {typeof item === 'string' ? item : item.name}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="package-reward">
                    <FaGem />
                    <span>Earn <strong>Bonus Coins</strong> on completion</span>
                  </div>

                  <button
                    className="book-expert-btn"
                    onClick={() => {
                      const token = localStorage.getItem("jwt_token");
                      if (!token) {
                        toast.info("Please log in to schedule this experience.");
                        navigate("/login");
                        return;
                      }
                      setShowBookingForm(true);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Schedule Experience
                  </button>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

const ReviewsSection = ({ tour }) => {
  const averageRating = parseFloat(tour.average_rating) || 0.0;
  const reviewCount = tour.review_count || 0;

  return (
    <section className="info-card section-animate">
      <div className="section-header">
        <FaStar className="section-icon" />
        <h3>Guest Feedback</h3>
      </div>
      
      <div className="review-summary-box">
        <div className="rating-score">
          <h4>{averageRating.toFixed(1)}</h4>
          <div className="rating-stars">
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} className={i < Math.round(averageRating) ? "star-full" : "star-empty"} />
            ))}
          </div>
          <p>{reviewCount} Verified Reviews</p>
        </div>
        <div className="rating-progress-grid">
           {[5,4,3,2,1].map(num => (
             <div key={num} className="progress-item">
               <span>{num}★</span>
               <div className="bar-container">
                 <div className="bar-fill" style={{ width: `${(num === 5 ? 85 : num === 4 ? 12 : 3)}%` }}></div>
               </div>
             </div>
           ))}
        </div>
      </div>

      <div className="individual-reviews">
        {tour.reviews && tour.reviews.length > 0 ? (
          tour.reviews.map((review, index) => (
            <div key={index} className="review-item-modern">
               <div className="review-auth-row">
                 <div className="auth-avatar">{review.user?.full_name?.charAt(0) || "U"}</div>
                 <div className="auth-meta">
                   <strong>{review.user?.full_name || "Adventurer"}</strong>
                   <span>Shared a review recently</span>
                 </div>
                 <div className="auth-rating">{typeof review.rating === 'number' ? review.rating : String(review.rating || '5').split(' ')[0]} ★</div>
               </div>
               <p>"{review.comment || 'An amazing journey with an exceptional guide!'}"</p>
            </div>
          ))
        ) : (
          <p className="no-data-centered">No reviews yet. Be the first to join this journey!</p>
        )}
      </div>
    </section>
  );
};

export default GuideProfileBook;
